import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { DatePicker } from 'primeng/datepicker';
import { NgHeaderTemplateDirective, NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { AuditEventSearchRequestDto } from '../../common/interfaces/audit-event-search-request-dto';
import { BusinessHistoryCategory } from '../../common/enums/business-history-category.enum';
import { SsoUserDetailsDto } from '../../common/interfaces/sso-user-details-dto';
import { SecurityDomain } from '../../common/enums/security-domain';
import { PaginatedSearchResponse } from '../../common/interfaces/paginated-search-response';
import { getEventsByCategory } from '../../common/utils/business-history-category-util';
import { getEnterpriseLabels } from '../../common/utils/enterprise-labels.util';
import { defaultAuditDateRange } from '../../common/utils/audit-date-util';
import { catchError, debounceTime, distinctUntilChanged, filter, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { AccountsSearchService } from '../../common/services/accounts-search.service';
import { ToastrService } from 'ngx-toastr';
import { AccountsFilterDto } from '../../common/interfaces/accounts-filter-dto';
import { FormsModule } from '@angular/forms';
import { AdminAuditEventType } from '../../common/enums/admin-audit-event-type.enum';
import { FilterPillComponent } from '../filter-pill/filter-pill.component';
import { SysAdminAuditEventType } from '../../common/enums/sysadmin-audit-event-type.enum';
import { UserRepresentationDto } from '../../common/interfaces/user-representation-dto';
import { envToken } from '../../common/tokens/env-token';

@Component({
    selector: 'lib-audit-log-filter',
    imports: [
        AsyncPipe,
        DatePicker,
        NgHeaderTemplateDirective,
        NgIf,
        NgLabelTemplateDirective,
        NgOptionTemplateDirective,
        NgSelectComponent,
        FormsModule,
        FilterPillComponent
    ],
    standalone: true,
    templateUrl: './audit-log-filter.component.html',
    styleUrl: './audit-log-filter.component.scss'
})
export class AuditLogFilterComponent implements OnInit, OnDestroy {
    private readonly accountSearchService = inject(AccountsSearchService);
    private readonly toastrService = inject(ToastrService);
    private env = inject(envToken);

    private DATE_RANGE_LIMIT = 30;
    private SOURCE_SYSTEM_ENTERPRISE_ADMIN = 'ENTERPRISE_ADMIN';
    private readonly SEARCH_RESULT_LIMIT = 5;

    @Input() enterpriseId = 0;
    @Input() enterpriseLabels = getEnterpriseLabels(SecurityDomain.MODMED);
    @Input() eventCategory?: BusinessHistoryCategory;
    @Input() enterpriseMap? = new Map<number, string>();
    @Input() dataIdentifierPrefix = 'audit-log';

    @Output() auditLogFilterChange = new EventEmitter<AuditEventSearchRequestDto>;

    @ViewChild('userSearchInput') userSearchInput!: ElementRef;
    @ViewChild('enterpriseSearchInput') enterpriseSearchInput!: ElementRef;

    readonly userSearch$ = new Subject<string>();
    readonly enterpriseSearch$ = new Subject<string>();

    users$?: Observable<SsoUserDetailsDto[] | UserRepresentationDto[]>;
    enterprises$!: Observable<{ id: number, name: string }[]>;

    selectedDateRange: Date[] | null = null;
    selectedUser?: SsoUserDetailsDto | null = null;
    selectedEnterprise?: { id: number, name: string } | null = null;
    selectedEventName: string | null = null;
    eventNames: { value: string; label: string }[] = [];
    maxSelectableDate: Date = this.endOfToday();
    showFilters = false;
    filterLabel = 'All Filters';
    sourceSystem = this.SOURCE_SYSTEM_ENTERPRISE_ADMIN;

    protected destroyed$: Subject<void> = new Subject<void>();

    ngOnInit(): void {
        this.sourceSystem = this.env.sourceSystem;
        this.applyEventLabels();
        this.initUsers();
        this.initEnterprises();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    switchToggle(): void {
        this.showFilters = !this.showFilters;
        this.filterLabel = (this.showFilters ? 'Hide' : 'All') + ' Filters';
    }

    getUserLabel(user: SsoUserDetailsDto): string {
        return user ? `${user.lastName}, ${user.firstName} (${user.username})` : '';
    }

    onUserSearchInput(event: Event): void {
        const searchTerm = (event.target as HTMLInputElement).value;
        this.userSearch$.next(searchTerm);
    }

    onEnterpriseSearchInput(event: Event): void {
        const searchTerm = (event.target as HTMLInputElement).value;
        this.enterpriseSearch$.next(searchTerm);
    }

    focusSearchInput(): void {
        setTimeout(() => {
            if (this.userSearchInput) {
                this.userSearchInput.nativeElement.focus();
                this.userSearch$.next('');
            }
        });
    }

    focusEnterpriseSearchInput(): void {
        setTimeout(() => {
            if (this.enterpriseSearchInput) {
                this.enterpriseSearchInput.nativeElement.focus();
                this.enterpriseSearch$.next('');
            }
        });
    }

    applyFilters(): void {
        if (!this.validateDateRange()) return;

        const request = this.buildSearchRequest();
        this.auditLogFilterChange.emit(request);
    }

    clearFilters(): void {
        this.resetSelectedDate();
        this.selectedUser = null;
        this.selectedEnterprise = null;
        this.selectedEventName = null;
        this.auditLogFilterChange.emit({ partitionId: this.getPartitionId(), eventTimeRange: defaultAuditDateRange(this.DATE_RANGE_LIMIT) } as AuditEventSearchRequestDto);
    }

    getPartitionId(): number {
        const id = this.isOrgAdminApp() ? this.enterpriseId : (this.selectedEnterprise?.id ?? 0);
        return id > 0 ? id : 0;
    }

    loadAdminUsers(searchTerm = ''): Observable<PaginatedSearchResponse<SsoUserDetailsDto>> {
        return this.accountSearchService.search(this.enterpriseId, {
            searchTerm: searchTerm,
            isEnterpriseAdmin: true,
            page: 0,
            itemsPerPage: this.SEARCH_RESULT_LIMIT,
            sortBy: 'username',
            sortOrder: 'ASC'
        } as AccountsFilterDto);
    }

    loadEnterprises(searchTerm = ''): { id: number, name: string }[] {
        return [...this.enterpriseMap!.entries()]
            .filter(([, label]) => label.toLowerCase().includes(searchTerm.toLowerCase()))
            .slice(0, this.SEARCH_RESULT_LIMIT)
            .map(([id, name]) => ({ id, name }));
    }

    isOrgAdminApp(): boolean {
        return this.sourceSystem === this.SOURCE_SYSTEM_ENTERPRISE_ADMIN;
    }

    resetSelectedDate(): void {
        this.selectedDateRange = [];
        this.maxSelectableDate = this.endOfToday();
    }

    onDateRangeChange(): void {
        if (!this.selectedDateRange || !this.selectedDateRange[ 0 ]) {
            this.maxSelectableDate = this.endOfToday();
            return;
        }

        const start = this.selectedDateRange[ 0 ];
        const max = new Date(start.getFullYear(), start.getMonth(), start.getDate() + (this.DATE_RANGE_LIMIT - 1), 23, 59, 59, 999);

        const today = this.endOfToday();
        this.maxSelectableDate = max < today ? max : today;
    }

    validateDateRange(): boolean {
        if (!this.selectedDateRange || this.selectedDateRange.length === 0) return true;

        if (this.selectedDateRange.length !== 2) {
            this.toastrService.warning('Start and end date needs to be selected.');
            return false;
        }

        const [start, end] = this.selectedDateRange || [];
        if (!start || !end) {
            this.toastrService.warning('Start and end date needs to be selected.');
            return false;
        }
        const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
        const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);

        const diffMs = Math.abs(endDate.getTime() - startDate.getTime());
        const maxMs = this.DATE_RANGE_LIMIT * 24 * 60 * 60 * 1000;

        if (diffMs > maxMs) {
            this.toastrService.warning(`Date range must be ${this.DATE_RANGE_LIMIT} days or less.`);
            return false;
        }

        return true;
    }

    private buildSearchRequest(): AuditEventSearchRequestDto {
        return {
            userId: this.selectedUser?.id ?? null,
            eventType: this.selectedEventName ?? null,
            partitionId: this.getPartitionId(),
            eventTimeRange: this.buildDateRange()
        } as AuditEventSearchRequestDto;
    }

    private buildDateRange(): AuditEventSearchRequestDto['eventTimeRange'] {
        if (!this.selectedDateRange || this.selectedDateRange.length !== 2) {
            return defaultAuditDateRange(this.DATE_RANGE_LIMIT);
        }

        const [start, end] = this.selectedDateRange;
        const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
        const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);

        return {
            from: Math.floor(startDate.getTime() / 1000),
            to: Math.floor(endDate.getTime() / 1000)
        };
    }

    applyEventLabels(): void {
        const eventsByCategory = this.eventCategory ? getEventsByCategory(this.eventCategory) : [];
        this.eventNames = Object.entries(this.isOrgAdminApp() ? AdminAuditEventType : SysAdminAuditEventType)
            .filter(([key]) => {
                return this.eventCategory ? eventsByCategory.includes(key) : true;
            })
            .map(([key, value]) => ({
                value: key,
                label: value.replace('Universal ID', this.enterpriseLabels.universalIdLabel)
            }));
    }

    private initUsers(): void {
        const base$ = this.userSearch$
            .pipe(takeUntil(this.destroyed$))
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                filter(term => !term || term.length >= 3)
            );
        if (this.isOrgAdminApp()) {
            this.users$ = base$.pipe(
                switchMap(term =>
                    this.loadAdminUsers(term).pipe(
                        map(res => res.content),
                        catchError(() => this.handleError('Failed to retrieve users.'))
                    )
                )
            ) as Observable<SsoUserDetailsDto[]>;
        }
    }

    private initEnterprises(): void {
        this.enterprises$ = this.enterpriseSearch$
            .pipe(takeUntil(this.destroyed$))
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                filter(term => !term || term.length >= 3),
                map(term => this.loadEnterprises(term)),
                catchError(() => this.handleError('Failed to retrieve organizations.'))
            ) as Observable<{ id: number, name: string }[]>;
    }

    private handleError<T>(message: string): Observable<T> {
        this.toastrService.error(message, 'Error');
        return of([] as T);
    }

    private endOfToday(): Date {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    }
}
